import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';

import { Fitter, FitterStatistic, WeatherEvent } from '@sc/data-access-models';

import { HttpClient, HttpParams } from '@angular/common/http';
import {
  CreateAgendaFitterGQL,
  CreateAgendaFitterMutationVariables,
  CreateFitterGQL,
  CreateFitterMutationVariables,
  DeleteFitterGQL,
  DeleteFitterMutationVariables,
  FitterGQL,
  FitterQueryVariables,
  FitterStatisticsGQL,
  FitterStatisticsQueryVariables,
  FittersGQL,
  FittersQueryVariables,
  GetCoveredCitiesGQL,
  GetCoveredCitiesQueryVariables,
  GetFitterByManagerGQL,
  GetFitterByManagerQueryVariables,
  GetFittersByManagerGQL,
  SearchFittersGQL,
  SearchFittersQueryVariables,
  SearchMasterFittersGQL,
  SearchMasterFittersQueryVariables,
  UpdateAgendaFitterGQL,
  UpdateAgendaFitterMutationVariables,
  UpdateFitterCommentGQL,
  UpdateFitterGQL,
  UpdateFitterMutationVariables,
} from '@sc/data-access-graphql';
import { Observable } from 'rxjs';
import { InsightService } from '../insight';

@Injectable({
  providedIn: 'root',
})
export class FittersHttpService {
  private readonly uploadFileURL = `/api/partners/<brandId>/upload`;
  private readonly uploadFitterStatisticFileURL = `/api/partners/<brandId>/statistics/upload`;
  private readonly getFitterIdByNameURL = `/api/partners/<fitterName>/get-id`;
  private readonly uploadFitterWeatherURL = `/api/partners/weather-event`;

  constructor(
    private readonly fittersGQL: FittersGQL,
    private readonly fitterGQL: FitterGQL,
    private readonly searchFittersGQL: SearchFittersGQL,
    private readonly fitterStatisticsGQL: FitterStatisticsGQL,
    private readonly createFitterGQL: CreateFitterGQL,
    private readonly createAgendaFitterGQL: CreateAgendaFitterGQL,
    private readonly updateFitterGQL: UpdateFitterGQL,
    private readonly updateAgendaFitterGQL: UpdateAgendaFitterGQL,
    private readonly deleteFitterGQL: DeleteFitterGQL,
    private readonly updateFitterCommentGQL: UpdateFitterCommentGQL,
    private readonly getFittersByManagerGQL: GetFittersByManagerGQL,
    private readonly getFitterByManagerGQL: GetFitterByManagerGQL,
    private readonly getCoveredCitiesGQL: GetCoveredCitiesGQL,
    private readonly insightService: InsightService,
    private readonly httpClient: HttpClient,
    private readonly searchMasterFitter: SearchMasterFittersGQL
  ) {}

  fetchFitters(params: FittersQueryVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http fetchFitters start',
      properties: params,
    });
    return this.fittersGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(map((response) => response.data.fitters as Fitter[]));
  }

  fetchFitter(params: FitterQueryVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http fetchFitter start',
      properties: params,
    });

    return this.fitterGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(map((response) => response.data.fitter as Fitter));
  }

  getFitterIdByName(param: string) {
    return this.httpClient.get<{ brandId: string; fitterId: string }>(
      this.getFitterIdByNameURL.replace('<fitterName>', param),
      {
        headers: {
          requestName: 'fitters-http getFitterIdByName',
        },
      }
    );
  }

  searchFitters(params?: SearchFittersQueryVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http searchFitters start',
      properties: params,
    });

    return this.searchFittersGQL.fetch(params).pipe(
      map((response: any) => {
        return response.data.searchFitters as Fitter[];
      })
    );
  }

  searchMasterFitters(params?: SearchMasterFittersQueryVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http searchMasterFitters start',
      properties: params,
    });

    return this.searchMasterFitter.fetch(params).pipe(
      map((response: any) => {
        return response.data.searchMasterFitters as Fitter[];
      })
    );
  }

  getFittersByManager(): Observable<Fitter[]> {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http getFittersByManager start',
    });

    return this.getFittersByManagerGQL.watch(null).valueChanges.pipe(
      map((response: any) => {
        return response.data.getFittersByManager as Fitter[];
      })
    );
  }

  getFitterByManager(
    params: GetFitterByManagerQueryVariables
  ): Observable<Fitter> {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http getFitterByManager start',
    });

    return this.getFitterByManagerGQL
      .watch(params, { fetchPolicy: 'no-cache' })
      .valueChanges.pipe(
        map((response: any) => {
          return response.data.getFitterByManager as Fitter;
        })
      );
  }

  searchFitterStatistics(
    params?: FitterStatisticsQueryVariables
  ): Observable<FitterStatistic[]> {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http searchFitterStatistics start',
      properties: null,
    });

    return this.fitterStatisticsGQL.watch(params).valueChanges.pipe(
      map((response: any) => {
        return response.data.fitterStatistics as FitterStatistic[];
      })
    );
  }

  getCoveredCities(
    params?: GetCoveredCitiesQueryVariables
  ): Observable<{ postalCode: string; cities: string[] }[]> {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http getCoveredCities start',
      properties: null,
    });

    return this.getCoveredCitiesGQL.watch(params).valueChanges.pipe(
      map((response) => {
        return response.data.getCoveredCities as {
          postalCode: string;
          cities: string[];
        }[];
      })
    );
  }

  createFitter(params: CreateFitterMutationVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http createFitter start',
      properties: params,
    });
    if (params.fitter.email == null) {
      params.fitter.email = '';
    }

    return this.createFitterGQL
      .mutate(params, {
        update: (store) => {
          store.reset();
        },
      })
      .pipe(map((response) => response.data.createFitter as Fitter));
  }

  createAgendaFitter(params: CreateAgendaFitterMutationVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http createAgendaFitter start',
      properties: params,
    });
    if (params.fitter.email == null) {
      params.fitter.email = '';
    }

    return this.createAgendaFitterGQL
      .mutate(params, {
        update: (store) => {
          store.reset();
        },
      })
      .pipe(map((response) => response.data.createFitter as Fitter));
  }

  updateFitter(params: UpdateFitterMutationVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http updateFitter start',
      properties: params,
    });

    return this.updateFitterGQL
      .mutate(params, {
        update: (store) => {
          store.reset();
        },
      })
      .pipe(map((response) => response.data.updateFitter as Fitter));
  }

  updateAgendaFitter(params: UpdateAgendaFitterMutationVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http updateAgendaFitter start',
      properties: params,
    });

    return this.updateAgendaFitterGQL
      .mutate(params, {
        update: (store) => {
          store.reset();
        },
      })
      .pipe(map((response) => response.data.updateFitter as Fitter));
  }

  deleteFitter(params: DeleteFitterMutationVariables) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http deleteFitter start',
      properties: params,
    });

    return this.deleteFitterGQL
      .mutate(params, {
        update: (store) => {
          store.reset();
        },
      })
      .pipe(map((response) => response.data.deleteFitter as boolean));
  }

  uploadFitters(params: { file: File; brandId: string }) {
    const bodyFormData = new FormData();
    bodyFormData.append('file', params.file);
    return this.httpClient.post(
      this.uploadFileURL.replace('<brandId>', params.brandId),
      bodyFormData,
      {
        headers: {
          requestName: 'fitters-http uploadFitters',
        },
      }
    );
  }

  uploadFitterWeather(params: {
    event: WeatherEvent[];
  }): Observable<{ weatherEvents: WeatherEvent[] }> {
    return this.httpClient.post<{ weatherEvents: WeatherEvent[] }>(
      this.uploadFitterWeatherURL,
      { weatherEvents: params.event },
      {
        headers: {
          requestName: 'fitters-http uploadFitters',
        },
      }
    );
  }

  uploadFittersStatistic(params: {
    file: File;
    brandId: string;
    period: string;
  }) {
    const bodyFormData = new FormData();
    bodyFormData.append('file', params.file);
    const httpParams = new HttpParams().append('period', params.period);

    return this.httpClient.post(
      this.uploadFitterStatisticFileURL.replace('<brandId>', params.brandId),
      bodyFormData,
      {
        headers: {
          requestName: 'fitters-http uploadFittersStatistic',
          timeout: '30000',
        },
        params: httpParams,
      }
    );
  }

  updateFitterComment(fitterId: string, comment: string) {
    this.insightService.trackEvent({
      name: 'FrontApp fitters-http updateFitter start',
      properties: {
        fitterId,
        comment,
      },
    });

    return this.updateFitterCommentGQL
      .mutate(
        {
          id: fitterId,
          comment,
        },
        {
          update: (store) => {
            store.reset();
          },
        }
      )
      .pipe(map((response) => response.data.updateFitterComment as Fitter));
  }
}
